import { Box, Stack } from "@sembark-travel/ui-base"
import {
	GetFieldValue,
	SelectField,
	SwitchInputField,
	useFieldValue,
} from "@sembark-travel/ui-form"
import { useXHR } from "@sembark-travel/xhr"
import { useEffect } from "react"
import useSWR from "swr"
import {
	SelectWAPhoneNumber,
	SelectWATemplate,
	type TWATemplate,
	type TWhatsappBusinessAccountPhoneNumber,
	WATemplatePreviewBubble,
} from "../Integrations"
import { useCanSendOnWhatsApp } from "../WhatsAppMessages"
import { TCommentableType, whatsAppSourceKeyFor } from "./store"

/**
 * Attach a WhatsApp template to a task, to be sent to this record's
 * customer when the task falls due and nobody has resolved it.
 *
 * Opt-in per task: there is no setting behind this, and nothing is sent
 * unless somebody picks a template here.
 *
 * The variables are not editable. They are read from the record at the
 * moment the message goes out, so anything typed here a week earlier would
 * be a promise the send does not keep. What is shown is a preview of what
 * the customer will read.
 */
export function WhatsAppReminderFields({
	commentableType,
	commentableId,
	canMessageCustomer,
}: {
	commentableType: TCommentableType
	commentableId: number
	/**
	 * Whether this particular record has a customer to message. Only
	 * instalments are ambiguous -- they hang off hotel bookings and
	 * supplier payments as well as trips, and only a trip has a customer --
	 * so an instalment shows nothing unless its caller says otherwise.
	 */
	canMessageCustomer?: boolean
}) {
	const sourceKey = whatsAppSourceKeyFor(commentableType)
	const canSendOnWhatsApp = useCanSendOnWhatsApp()
	const { onChange: setTemplate } = useFieldValue<TWATemplate | undefined>(
		"wa_template"
	)
	const { onChange: setPhoneNumber } = useFieldValue<
		TWhatsappBusinessAccountPhoneNumber | undefined
	>("wa_phone_number")

	// Nothing to offer: this kind of record has no customer, this instalment
	// is not on a trip, or the tenant has no WhatsApp number to send from.
	if (
		!sourceKey ||
		!canSendOnWhatsApp ||
		(commentableType === "instalments" && canMessageCustomer !== true)
	) {
		return null
	}

	return (
		<Box padding="4" bgColor="subtle" rounded="md">
			<Stack gap="4">
				<SwitchInputField
					label="Message the customer on WhatsApp"
					help="Sent when this task falls due, and only if it is still unresolved by then"
					name="notify_customer_on_whatsapp"
				/>
				<GetFieldValue<boolean> name="notify_customer_on_whatsapp">
					{({ value: enabled }) =>
						enabled ? (
							<Stack gap="4">
								<SelectField
									select={SelectWATemplate}
									name="wa_template"
									label="WhatsApp Template"
									required
									sourceKey={sourceKey}
									expectedSourceKey={sourceKey}
									// A template with no variables needs no record to
									// fill it, so it is offered here too.
									includeSourceless
									fullWidth
									onChange={(option: TWATemplate | undefined) => {
										setTemplate(option)
										// The sender belongs to the template's account, so a
										// new template clears it and its own default fills in.
										setPhoneNumber(undefined)
									}}
								/>
								<GetFieldValue<TWATemplate | undefined> name="wa_template">
									{({ value: template }) =>
										template ? (
											<Stack gap="4">
												<SelectWAPhoneNumberWhenAmbiguous template={template} />
												{/* The message as this record would send it, with
												anything it cannot fill yet named below it. */}
												<WATemplatePreviewBubble
													template={template}
													mode="raw"
													sourceKey={sourceKey}
													sourceRecordId={commentableId}
												/>
											</Stack>
										) : null
									}
								</GetFieldValue>
							</Stack>
						) : null
					}
				</GetFieldValue>
			</Stack>
		</Box>
	)
}

/**
 * Meta will not send a template from a number outside its own business
 * account, so the sender is only a question when that account has more
 * than one connected number. With one, the server picks it and nobody is
 * asked.
 */
function SelectWAPhoneNumberWhenAmbiguous({
	template,
}: {
	template: TWATemplate
}) {
	const xhr = useXHR()
	const { value: phoneNumber, onChange: setPhoneNumber } = useFieldValue<
		TWhatsappBusinessAccountPhoneNumber | undefined
	>("wa_phone_number")
	const { data: numbers } = useSWR(
		`/wa-phone-numbers?wa_template_id=${template.id}`,
		() =>
			xhr
				.get<{
					data: Array<TWhatsappBusinessAccountPhoneNumber>
				}>("/wa-phone-numbers", {
					params: { wa_template_id: template.id, limit: 20 },
				})
				.then((resp) => resp.data.data),
		{ revalidateOnFocus: false }
	)

	// A template can name its own default sender, which the automation forms
	// already pre-select and the server falls back to. Shown picked rather
	// than hidden, so the operator sees where it goes out from and can change
	// it. Only a connected number is a choice at all; the server passes over
	// a disconnected default the same way.
	useEffect(() => {
		if (!numbers || phoneNumber) {
			return
		}

		const defaultSender = numbers.find(
			(number) =>
				number.id === template.wa_phone_number_id &&
				number.status === "CONNECTED"
		)

		if (defaultSender) {
			setPhoneNumber(defaultSender)
		}
	}, [numbers, phoneNumber, setPhoneNumber, template.wa_phone_number_id])

	if (!numbers || numbers.length < 2) {
		return null
	}

	return (
		<SelectField
			select={SelectWAPhoneNumber}
			name="wa_phone_number"
			label="Send From"
			required
			waTemplateId={template.id}
			fullWidth
		/>
	)
}
